import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usuariosApi } from '../api/usuarios';
import { useIsAuthenticated } from './useAuth';
import type { 
  UsuariosFilters, 
  CriarUsuarioRequest, 
  AtualizarUsuarioRequest,
  AlterarStatusRequest,
  RedefinirSenhaRequest,
  AlterarSenhaRequest
} from '../types/usuarios';

// Keys para cache do React Query
export const usuariosKeys = {
  all: ['usuarios'] as const,
  lists: () => [...usuariosKeys.all, 'list'] as const,
  list: (filters: UsuariosFilters) => [...usuariosKeys.lists(), filters] as const,
  detail: (id: number) => [...usuariosKeys.all, 'detail', id] as const,
  stats: () => [...usuariosKeys.all, 'stats'] as const,
};

// Hook para listar usuários
export function useUsuarios(filters: UsuariosFilters = {}) {
  const { isAuthenticated } = useIsAuthenticated();
  
  return useQuery({
    queryKey: usuariosKeys.list(filters),
    queryFn: () => usuariosApi.getUsuarios(filters),
    select: (response) => response.data,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchInterval: 30000,
  });
}

// Hook para obter usuário específico
export function useUsuario(id: number) {
  const { isAuthenticated } = useIsAuthenticated();
  
  return useQuery({
    queryKey: usuariosKeys.detail(id),
    queryFn: () => usuariosApi.getUsuario(id),
    select: (response) => response.data.data,
    enabled: !!id && isAuthenticated, // Só executa se tiver ID e estiver autenticado
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

// Hook para estatísticas
export function useEstatisticasUsuarios() {
  const { isAuthenticated } = useIsAuthenticated();
  
  return useQuery({
    queryKey: usuariosKeys.stats(),
    queryFn: () => usuariosApi.getEstatisticas(),
    select: (response) => response.data.data,
    enabled: isAuthenticated, // Só executa se estiver autenticado
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 5,
  });
}

// Hook para criar usuário
export function useCriarUsuario() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CriarUsuarioRequest) => usuariosApi.criarUsuario(data),
    onSuccess: () => {
      // Invalida cache de usuários e estatísticas
      queryClient.invalidateQueries({ queryKey: usuariosKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usuariosKeys.stats() });
    },
  });
}

// Hook para atualizar usuário
export function useAtualizarUsuario() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarUsuarioRequest }) =>
      usuariosApi.atualizarUsuario(id, data),
    onSuccess: (_, variables) => {
      // Invalida cache específico e listas
      queryClient.invalidateQueries({ queryKey: usuariosKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: usuariosKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usuariosKeys.stats() });
    },
  });
}

// Hook para alterar status
export function useAlterarStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AlterarStatusRequest }) =>
      usuariosApi.alterarStatus(id, data),
    onSuccess: (_, variables) => {
      // Invalida cache específico e listas
      queryClient.invalidateQueries({ queryKey: usuariosKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: usuariosKeys.lists() });
      queryClient.invalidateQueries({ queryKey: usuariosKeys.stats() });
    },
  });
}

// Hook para redefinir senha
export function useRedefinirSenha() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: RedefinirSenhaRequest }) =>
      usuariosApi.redefinirSenha(id, data),
    onSuccess: (_, variables) => {
      // Apenas invalida o cache do usuário específico
      queryClient.invalidateQueries({ queryKey: usuariosKeys.detail(variables.id) });
    },
  });
}

// Hook para alterar própria senha
export function useAlterarSenha() {
  return useMutation({
    mutationFn: (data: AlterarSenhaRequest) => usuariosApi.alterarSenha(data),
  });
}

// Hook para alterar senha de usuário manualmente (admin)
export function useAlterarSenhaManual() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: RedefinirSenhaRequest }) =>
      usuariosApi.alterarSenhaManual(id, data),
    onSuccess: (_, variables) => {
      // Apenas invalida o cache do usuário específico
      queryClient.invalidateQueries({ queryKey: usuariosKeys.detail(variables.id) });
    },
  });
}

// Hook para invalidar cache de usuários
export function useInvalidateUsuarios() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: usuariosKeys.all });
  };
}